import type {
  AutoGradablePrompt,
  BoxViewDocumentAnnotationPart,
  BoxViewDocumentAnnotationSchema,
  CmlContentType,
  Content,
  FileUploadSchema,
  FileUploadSubmissionPartResponse,
  FormElement,
  HtmlContentType,
  MultiLineReviewPart,
  MultiLineReviewSchema,
  OffPlatformPartResponse,
  OffPlatformSchema,
  OptionsOption,
  OptionsReviewPart,
  OptionsReviewSchema,
  PlagiarismConfig,
  PlainTextSchema,
  PlainTextSubmissionPartResponse,
  Prompt,
  ResponseScore,
  ReviewPart,
  ReviewPartSchemaDetails,
  ReviewPrompt,
  ReviewResponse,
  RichTextSchema,
  RichTextSubmissionPartResponse,
  SingleLineReviewPart,
  SingleLineReviewSchema,
  SubmissionPartResponse,
  SubmissionPartSchemaDetails,
  SubmissionPrompt,
  UrlSchema,
  UrlSubmissionPartResponse,
  YesNoReviewPart,
  YesNoReviewSchema,
} from 'bundles/compound-assessments/components/api/types/CompoundAssessmentsForm';
import { SubmissionPartResponseTypeNames, typeNames } from 'bundles/compound-assessments/constants';
import type {
  AssignmentRubric as NewAssignmentRubric,
  AutoGradedQuestionElement as NewAutoGradedQuestionElement,
  AutoGradedQuestionElementScore as NewAutoGradedQuestionElementScore,
  AutoGradedQuestionValue as NewAutoGradedQuestionValue,
  CmlContent as NewCmlContent,
  Content as NewContent,
  HtmlContent as NewHtmlContent,
  ManuallyGradedQuestionElement as NewManuallyGradedQuestionElement,
  ManuallyGradedQuestionElementScore as NewManuallyGradedQuestionElementScore,
  ManuallyGradedQuestionSubmissionPrompt as NewManuallyGradedQuestionSubmissionPrompt,
  ManuallyGradedQuestionSubmissionResponse as NewManuallyGradedQuestionSubmissionResponse,
  PlagiarismConfig as NewPlagiarismConfig,
  QuestionRubric as NewQuestionRubric,
  QuestionRubricElement as NewQuestionRubricElement,
  QuestionRubricPrompt as NewQuestionRubricPrompt,
  QuestionRubricPromptSchemaDetails as NewQuestionRubricPromptSchemaDetails,
  QuestionRubricPromptSchemaDetailsMultiLineInput as NewQuestionRubricPromptSchemaDetailsMultiLineInput,
  QuestionRubricPromptSchemaDetailsOptions as NewQuestionRubricPromptSchemaDetailsOptions,
  QuestionRubricPromptSchemaDetailsYesNo as NewQuestionRubricPromptSchemaDetailsYesNo,
  QuestionRubricResponseBoxViewDocumentAnnotation as NewQuestionRubricResponseBoxViewDocumentAnnotation,
  QuestionRubricResponseContainer as NewQuestionRubricResponseContainer,
  QuestionRubricResponseMultiLineInput as NewQuestionRubricResponseMultiLineInput,
  QuestionRubricResponseOptions as NewQuestionRubricResponseOptions,
  QuestionRubricResponseSingleLineInput as NewQuestionRubricResponseSingleLineInput,
  QuestionRubricResponseYesNo as NewQuestionRubricResponseYesNo,
  SubmissionPartResponse as NewSubmissionPartResponse,
  SubmissionPartSchemaDetails as NewSubmissionPartSchemaDetails,
  SubmissionPartResponseRichTextResponse,
  SubmissionPartSchemaDetailsFileUploadSchema,
  SubmissionPartSchemaDetailsUrlSchema,
  SubmissionQuestionBase,
} from 'bundles/grading/api/types';
import {
  ContentTypeEnum as NewContentTypeNameEnum,
  PromptTypeNameEnum as NewPromptTypeNameEnum,
  SubmissionPartResponseTypeNameEnum as NewSubmissionPartResponseTypeNameEnum,
  SubmissionPartSchemaDetailsCourierTypeNameEnum as NewSubmissionPartSchemaDetailsCourierTypeNameEnum,
  ReviewPartSchemaDetailsCourierTypeNameEnum,
  ReviewPartTypeNameEnum,
} from 'bundles/grading/api/types';

/**
 * Processes new cml and html content for FormPart usage
 *
 * @param { NewContent } newContent
 * @returns Content
 */
export const processContent = (newContent: NewContent): Content => {
  const shapedContent =
    newContent.courierTypeName === NewContentTypeNameEnum.CmlContent
      ? {
          typeName: 'cmlContent' as const,
          definition: {
            dtdId: (newContent as NewCmlContent).dtdId,
            value: (newContent as NewCmlContent).cmlValue,
          } as CmlContentType,
        }
      : {
          typeName: 'htmlContent' as const,
          definition: {
            html: (newContent as NewHtmlContent).html,
          } as HtmlContentType,
        };
  return shapedContent;
};

/**
 * Processes new SubmissionPartSchemaDetails for FormPart usage.
 * Includes different question submission types
 *
 * @param { NewSubmissionPartSchemaDetails } newDetails
 * @returns SubmissionPartSchemaDetails
 */
export const processSubmissionSchemaDetails = (
  newDetails: NewSubmissionPartSchemaDetails
): SubmissionPartSchemaDetails => {
  let shapedDetails;
  switch (newDetails.courierTypeName) {
    case NewSubmissionPartSchemaDetailsCourierTypeNameEnum.RichTextSchema:
      shapedDetails = {
        typeName: NewSubmissionPartSchemaDetailsCourierTypeNameEnum.RichTextSchema,
        definition: {} as RichTextSchema,
      };
      break;
    case NewSubmissionPartSchemaDetailsCourierTypeNameEnum.FileUploadSchema:
      shapedDetails = {
        typeName: NewSubmissionPartSchemaDetailsCourierTypeNameEnum.FileUploadSchema,
        definition: {
          required: (newDetails as SubmissionPartSchemaDetailsFileUploadSchema).required,
        } as FileUploadSchema,
      };
      break;
    case NewSubmissionPartSchemaDetailsCourierTypeNameEnum.UrlSchema:
      shapedDetails = {
        typeName: NewSubmissionPartSchemaDetailsCourierTypeNameEnum.UrlSchema,
        definition: {
          allowedUrls: (newDetails as SubmissionPartSchemaDetailsUrlSchema).allowedUrls,
        } as UrlSchema,
      };
      break;
    case NewSubmissionPartSchemaDetailsCourierTypeNameEnum.OffPlatformSchema:
      shapedDetails = {
        typeName: NewSubmissionPartSchemaDetailsCourierTypeNameEnum.OffPlatformSchema,
        definition: {} as OffPlatformSchema,
      };
      break;
    case NewSubmissionPartSchemaDetailsCourierTypeNameEnum.PlainTextSchema:
    default:
      shapedDetails = {
        typeName: NewSubmissionPartSchemaDetailsCourierTypeNameEnum.PlainTextSchema,
        definition: {} as PlainTextSchema,
      };
      break;
  }
  return shapedDetails;
};

export const processPlagiarismConfig = (newPlagiarismConfig?: NewPlagiarismConfig | null): PlagiarismConfig => {
  return {
    plagiarismCheckEnable: newPlagiarismConfig?.plagiarismCheckEnable,
  } as PlagiarismConfig;
};

/**
 * Parses the new value component of an auto-graded question since incoming
 * data includes stringified nested objects that must be parsed for
 * FormPart usage
 *
 * @param { NewAutoGradedQuestionValue } value
 * @returns NewAutoGradedQuestionValue
 */
export const parseValueObject = (value: NewAutoGradedQuestionValue): NewAutoGradedQuestionValue => {
  return {
    ...value,
    variant: {
      ...value?.variant,
      definition: JSON.parse(value.variant.definition),
    },
    effectiveResponse: {
      ...value.effectiveResponse,
      response: JSON.parse(value.effectiveResponse.response),
    },
    feedback: {
      ...value.feedback,
      definition: JSON.parse(value.feedback.definition),
    },
  } as NewAutoGradedQuestionValue;
};

/**
 * Parses graphQl rubric prompt schemaDetails and returns ReviewPartSchemaDetails object compatible with FormPart
 * @param value NewQuestionRubricPromptSchemaDetails
 * @returns ReviewPartSchemaDetails
 */
export const processReviewPartSchemaDetails = (
  value: NewQuestionRubricPromptSchemaDetails
): ReviewPartSchemaDetails => {
  let definition;
  switch (value.courierTypeName) {
    case ReviewPartSchemaDetailsCourierTypeNameEnum.BoxViewDocumentAnnotationReviewSchema:
      definition = {} as BoxViewDocumentAnnotationSchema;
      break;
    case ReviewPartSchemaDetailsCourierTypeNameEnum.MultiLineInputReviewSchema:
      definition = {
        points: (value as NewQuestionRubricPromptSchemaDetailsMultiLineInput).points,
      } as MultiLineReviewSchema;
      break;
    case ReviewPartSchemaDetailsCourierTypeNameEnum.OptionsReviewSchema:
      definition = {
        options: (value as NewQuestionRubricPromptSchemaDetailsOptions).options.map(
          (item): OptionsOption => ({
            id: item.optionId,
            display: processContent(item.display),
            points: item.points || undefined,
          })
        ),
        isScored: (value as NewQuestionRubricPromptSchemaDetailsOptions).isScored,
      } as OptionsReviewSchema;
      break;
    case ReviewPartSchemaDetailsCourierTypeNameEnum.SingleLineInputReviewSchema:
      definition = {} as SingleLineReviewSchema;
      break;
    case ReviewPartSchemaDetailsCourierTypeNameEnum.YesNoReviewSchema:
    default:
      definition = {
        points: (value as NewQuestionRubricPromptSchemaDetailsYesNo).pointsYN,
      } as YesNoReviewSchema;
  }

  return {
    typeName: value.courierTypeName,
    definition,
  };
};

/**
 * Parses graphQl rubric response reviewPart and returns ReviewResponse object compatible with FormPart
 * @param val NewQuestionRubricResponseContainer
 * @returns ReviewResponse
 */
export const processNewResponseReviewPart = (val: NewQuestionRubricResponseContainer): ReviewResponse => {
  let reviewPart: ReviewPart;
  const richTextInput = (val.reviewPart as NewQuestionRubricResponseMultiLineInput).richTextInput;
  switch (val.reviewPart.courierTypeName) {
    case ReviewPartTypeNameEnum.BoxViewDocumentAnnotation:
      reviewPart = {
        typeName: typeNames.BOXVIEWDOCUMENTANNOTATION,
        definition: {
          fileId: (val.reviewPart as NewQuestionRubricResponseBoxViewDocumentAnnotation).fileId,
          annotationComplete: (val.reviewPart as NewQuestionRubricResponseBoxViewDocumentAnnotation).annotationComplete,
        } as BoxViewDocumentAnnotationPart,
      };
      break;
    case ReviewPartTypeNameEnum.MultiLineInput:
      reviewPart = {
        typeName: typeNames.MULTILINEINPUT,
        definition: {
          input: (val.reviewPart as NewQuestionRubricResponseMultiLineInput).input || '',
          score: (val.reviewPart as NewQuestionRubricResponseMultiLineInput).score,
          richTextInput: richTextInput
            ? {
                typeName: 'cml' as const,
                definition: {
                  dtdId: (richTextInput as NewCmlContent).dtdId,
                  value: (richTextInput as NewCmlContent).cmlValue,
                },
              }
            : undefined,
        } as MultiLineReviewPart,
      };
      break;
    case ReviewPartTypeNameEnum.Options:
      reviewPart = {
        typeName: typeNames.OPTIONS,
        definition: {
          choice: (val.reviewPart as NewQuestionRubricResponseOptions).optionChoice,
          richTextInput: richTextInput
            ? {
                typeName: 'cml' as const,
                definition: {
                  dtdId: (richTextInput as NewCmlContent).dtdId,
                  value: (richTextInput as NewCmlContent).cmlValue,
                },
              }
            : undefined,
        } as OptionsReviewPart,
      };
      break;
    case ReviewPartTypeNameEnum.SingleLineInput:
      reviewPart = {
        typeName: typeNames.SINGLELINEINPUT,
        definition: {
          input: (val.reviewPart as NewQuestionRubricResponseSingleLineInput).input,
        } as SingleLineReviewPart,
      };
      break;
    case ReviewPartTypeNameEnum.YesNo:
    default:
      reviewPart = {
        typeName: typeNames.YESNO,
        definition: {
          choice: (val.reviewPart as NewQuestionRubricResponseYesNo).yesNoChoice,
          explanation: (val.reviewPart as NewQuestionRubricResponseYesNo).explanation,
        } as YesNoReviewPart,
      };
  }
  return { reviewPart, graderType: val.graderType, aiSuggestionId: val.aiSuggestionId || undefined };
};
/**
 * Processes new manually-graded "prompt" or auto-graded "value" objects
 * into existing Prompt object for FormPart usage
 *
 * @param { NewManuallyGradedQuestionSubmissionPrompt | NewAutoGradedQuestionValue } newPromptOrValue
 * @returns Prompt
 */
export const processNewPrompt = (
  newPromptOrValue: NewManuallyGradedQuestionSubmissionPrompt | NewAutoGradedQuestionValue | NewQuestionRubricPrompt
): Prompt => {
  // TODO: Temporary use of "courierTypeName" key check to assist with linting as ...formElementPromptRequiringResponseElement query is not yet implemented
  const courierTypeName = 'courierTypeName' in newPromptOrValue ? newPromptOrValue.courierTypeName : null;

  let shapedPrompt;
  let submissionPromptDefinition: SubmissionPrompt;
  let rubricPromptDefinition: ReviewPrompt;
  switch (courierTypeName) {
    // manual graded question
    case NewPromptTypeNameEnum.SubmissionPrompt:
      submissionPromptDefinition = {
        submissionPartSchema: {
          id: newPromptOrValue.promptId,
          graderType: (newPromptOrValue as NewManuallyGradedQuestionSubmissionPrompt).graderType,
          promptContent: processContent((newPromptOrValue as NewManuallyGradedQuestionSubmissionPrompt).promptContent),
          submissionPartSchemaDetails: processSubmissionSchemaDetails(
            (newPromptOrValue as NewManuallyGradedQuestionSubmissionPrompt).submissionPartSchemaDetails
          ),
          plagiarismConfig: processPlagiarismConfig(
            (newPromptOrValue as NewManuallyGradedQuestionSubmissionPrompt).plagiarismConfig
          ),
        },
      };
      shapedPrompt = {
        typeName: typeNames.SUBMISSION_PROMPT,
        definition: submissionPromptDefinition,
      };
      break;

    // rubric
    case NewPromptTypeNameEnum.ReviewPrompt:
      rubricPromptDefinition = {
        reviewPromptSchema: {
          id: newPromptOrValue.promptId,
          graderType: (newPromptOrValue as NewQuestionRubricPrompt).graderType,
          reviewPartSchemaDetails: processReviewPartSchemaDetails(
            (newPromptOrValue as NewQuestionRubricPrompt).reviewPartSchemaDetails
          ),
          submissionPartId: (newPromptOrValue as NewQuestionRubricPrompt).submissionPartId as string | undefined,
          promptContent: processContent((newPromptOrValue as NewQuestionRubricPrompt).promptContent),
        },
      };
      shapedPrompt = {
        typeName: typeNames.REVIEW_PROMPT,
        definition: rubricPromptDefinition,
      };
      break;

    // auto graded question
    case NewPromptTypeNameEnum.AutoGradablePrompt:
    default:
      shapedPrompt = {
        typeName: typeNames.AUTO_GRADABLE_PROMPT,
        definition: {
          value: parseValueObject(newPromptOrValue as NewAutoGradedQuestionValue),
        } as AutoGradablePrompt,
      };
  }
  return shapedPrompt;
};

/**
 * Processes new submissionPartResponse data for FormPart usage
 *
 * @param { NewSubmissionPartResponse } newSubmissionPartResponse
 * @returns SubmissionPartResponse
 */
export const processSubmissionPartResponse = (
  newSubmissionPartResponse: NewSubmissionPartResponse
): SubmissionPartResponse => {
  let submissionPartResponse;

  switch (newSubmissionPartResponse.courierTypeName) {
    case NewSubmissionPartResponseTypeNameEnum.FileUploadResponse:
      submissionPartResponse = {
        typeName: SubmissionPartResponseTypeNames.FILE_UPLOAD_RESPONSE,
        definition: {
          ...newSubmissionPartResponse,
        } as FileUploadSubmissionPartResponse,
      };
      break;
    case NewSubmissionPartResponseTypeNameEnum.OffPlatformResponse:
      submissionPartResponse = {
        typeName: SubmissionPartResponseTypeNames.OFF_PLATFORM_RESPONSE,
        definition: {
          ...newSubmissionPartResponse,
        } as OffPlatformPartResponse,
      };
      break;
    case NewSubmissionPartResponseTypeNameEnum.RichTextResponse:
      submissionPartResponse = {
        typeName: SubmissionPartResponseTypeNames.RICH_TEXT_RESPONSE,
        definition: {
          richText: processContent((newSubmissionPartResponse as SubmissionPartResponseRichTextResponse).richText),
        } as RichTextSubmissionPartResponse,
      };
      break;
    case NewSubmissionPartResponseTypeNameEnum.UrlResponse:
      submissionPartResponse = {
        typeName: SubmissionPartResponseTypeNames.URL_RESPONSE,
        definition: {
          ...newSubmissionPartResponse,
        } as UrlSubmissionPartResponse,
      };
      break;
    case NewSubmissionPartResponseTypeNameEnum.PlainTextResponse:
    default:
      submissionPartResponse = {
        typeName: SubmissionPartResponseTypeNames.PLAIN_TEXT_RESPONSE,
        definition: {
          ...newSubmissionPartResponse,
        } as PlainTextSubmissionPartResponse,
      };
      break;
  }
  return submissionPartResponse;
};

/**
 * Returns the "partId" of both manually-graded and auto-graded
 * questions
 *
 * @param { SubmissionQuestionBase } question
 * @returns string
 */
export const getQuestionPartId = (question: SubmissionQuestionBase): string => {
  return question.__typename === 'GroupGrading_QuestionManuallyGraded'
    ? (
        (question.formElementNode?.element as NewManuallyGradedQuestionElement)
          .prompt as NewManuallyGradedQuestionSubmissionPrompt
      ).promptId // TODO: Account for ReviewPrompt (currently same data structure for its id)
    : (question.formElementNode?.element as NewAutoGradedQuestionElement).value.promptId;
};

/**
 * Extracts rubric prompt
 * @param reviewPart NewAssignmentRubric | NewQuestionRubric
 * @returns NewQuestionRubricPrompt
 */
export const getRubricPrompt = (reviewPart: NewAssignmentRubric | NewQuestionRubric): NewQuestionRubricPrompt => {
  return (reviewPart.formElementNode?.element as NewQuestionRubricElement).prompt as NewQuestionRubricPrompt;
};

/**
 * Extracts rubric response
 * @param reviewPart NewAssignmentRubric | NewQuestionRubric
 * @returns NewQuestionRubricResponseContainer
 */
export const getRubricResponse = (
  reviewPart: NewAssignmentRubric | NewQuestionRubric
): NewQuestionRubricResponseContainer => {
  return (reviewPart.formElementNode?.element as NewQuestionRubricElement)
    .response as NewQuestionRubricResponseContainer;
};

/**
 * Returns the rubric id
 *
 * @param reviewPart NewAssignmentRubric | NewQuestionRubric
 * @returns string
 */
export const getRubricId = (reviewPart: NewAssignmentRubric | NewQuestionRubric): string => {
  const prompt = (reviewPart.formElementNode?.element as NewQuestionRubricElement)?.prompt as NewQuestionRubricPrompt;
  return prompt.promptId;
};

/**
 *  Get type of the rubric based on reviewPartSchemaDetails
 * @param reviewPart  NewAssignmentRubric | NewQuestionRubric
 * @returns ReviewPartSchemaDetailsCourierTypeNameEnum
 */
export const getRubricSchemaType = (
  reviewPart: NewAssignmentRubric | NewQuestionRubric
): ReviewPartSchemaDetailsCourierTypeNameEnum => {
  const prompt = getRubricPrompt(reviewPart);
  return prompt.reviewPartSchemaDetails.courierTypeName;
};

/**
 * Processes the new score data into the ResponseScore object
 *
 * @param { NewManuallyGradedQuestionElementScore | NewAutoGradedQuestionElementScore } score
 * @returns ResponseScore
 */
export const processResponseScore = (
  score: NewManuallyGradedQuestionElementScore | NewAutoGradedQuestionElementScore
): ResponseScore => {
  return {
    maxPoints: score.maxPoints,
    points: score.points ? score.points : 0,
  };
};

/**
 * Processes the FormElement type for new ManuallyGradedQuestionElement
 * (Manually Graded Question)
 *
 * @param { NewManuallyGradedQuestionElement } questionElement
 * @returns FormElement
 */
export const processManuallyGradedFormElement = (questionElement: NewManuallyGradedQuestionElement): FormElement => {
  return {
    typeName: typeNames.PROMPT_WITH_RESPONSE_ELEMENT,
    definition: {
      prompt: processNewPrompt(questionElement.prompt as NewManuallyGradedQuestionSubmissionPrompt),
      responseId: questionElement.responseId,
      response: {
        typeName: typeNames.SUBMISSION_RESPONSE,
        definition: {
          submissionPartResponse: processSubmissionPartResponse(
            (questionElement.response as NewManuallyGradedQuestionSubmissionResponse).submissionPartResponse
          ),
        },
      },
      responseMetadata: {},
      score: questionElement.score ? processResponseScore(questionElement.score) : undefined,
    },
  };
};

/**
 * Processes the FormElement type for new AutoGradedQuestionElement
 * (Auto Graded Question).
 *
 * Note that the FormPart still expects a PromptWithResponseElement
 * typeName for the root of the FormElement even for AutoGradedQuestionElements
 *
 * @param { NewAutoGradedQuestionElement } questionElement
 * @returns FormElement
 */
export const processAutoGradedFormElement = (questionElement: NewAutoGradedQuestionElement): FormElement => {
  return {
    typeName: typeNames.PROMPT_WITH_RESPONSE_ELEMENT,
    definition: {
      prompt: processNewPrompt(questionElement.value),
      responseId: questionElement.value.promptId,
      response: {
        typeName: typeNames.AUTO_GRADABLE_RESPONSE,
        definition: {
          value: parseValueObject(questionElement.value)?.effectiveResponse.response,
        },
      },
      responseMetadata: {},
      score: questionElement.score ? processResponseScore(questionElement.score) : undefined,
    },
  };
};
